import React from "react";
import {
  FaInfoCircle,
  FaMapMarkedAlt,
  FaRedo,
  FaShareAltSquare,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import SharedEventCard from "./components/SharedEventCard";
import SharedEventDetailsModal from "./components/SharedEventDetailsModal";
import SharedEventsEmptyState from "./components/SharedEventsEmptyState";
import SharedEventsHero from "./components/SharedEventsHero";
import SharedEventsMapModal from "./components/SharedEventsMapModal";
import SharedEventsSkeleton from "./components/SharedEventsSkeleton";
import SharedEventsStats from "./components/SharedEventsStats";
import SharedEventsToolbar from "./components/SharedEventsToolbar";
import { useSharedEventsPage } from "./hooks/useSharedEventsPage";
import { getSharedEventKey } from "./utils/sharedEventsUtils";
import AdherentEventSelector from "../EventPage/components/AdherentEventSelector";

function SharedEventsPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const {
    events,
    filteredEvents,
    filters,
    selectedEvent,
    isDetailsOpen,
    isMapOpen,
    loading,
    refreshing,
    copyingIds,
    copiedIds,
    activeFiltersCount,
    stats,
    fetchEvents,
    handleFilterChange,
    resetFilters,
    handleCopyEvent,
    handleOpenDetails,
    handleCloseDetails,
    setIsMapOpen,
    handleDepartmentSelect,
    adherents,
    selectedAdherentId,
    adherentsLoading,
    selectAdherent,
    isEventCopyingForSelectedAdherent,
    isEventCopiedForSelectedAdherent,
  } = useSharedEventsPage();

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <SharedEventsHero
        totalEvents={stats.total}
        upcomingEvents={stats.upcoming}
        departmentsCount={stats.departmentsCount}
        onBack={() => navigate("/")}
      />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                <FaShareAltSquare />
                Partage national
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Événements partagés
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Consulte les événements proposés par d'autres utilisateurs,
                filtre par département, catégorie ou niveau, puis copie ceux qui
                t'intéressent dans ton agenda personnel.
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:min-w-[380px]">
              <button
                type="button"
                onClick={() => setIsMapOpen(true)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-300/60 transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950 dark:shadow-black/20"
              >
                <FaMapMarkedAlt />
                Carte des départements
              </button>

              <button
                type="button"
                onClick={fetchEvents}
                disabled={refreshing}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-sky-300/40 dark:hover:text-sky-100"
              >
                <FaRedo className={refreshing ? "animate-spin" : ""} />
                {refreshing ? "Actualisation..." : "Actualiser"}
              </button>
            </div>
          </div>

          <div className="mt-5 rounded-[1.5rem] border border-sky-100 bg-sky-50/80 p-4 dark:border-sky-300/10 dark:bg-sky-500/10">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm dark:bg-white/10 dark:text-sky-100">
                <FaInfoCircle />
              </div>

              <div>
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Comment ça marche ?
                </p>
                <p className="mt-1 text-sm font-medium leading-6 text-slate-600 dark:text-white/60">
                  Les événements visibles ici sont partagés par d'autres
                  utilisateurs. Tu peux consulter les détails puis copier
                  l'événement dans ton propre agenda judoka.
                </p>
              </div>
            </div>
          </div>

          <SharedEventsStats stats={stats} />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {!adherentsLoading && adherents.length > 0 && (
          <AdherentEventSelector
            adherents={adherents}
            selectedId={selectedAdherentId}
            onSelect={selectAdherent}
          />
        )}
        <SharedEventsToolbar
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
          activeFiltersCount={activeFiltersCount}
          resultsCount={filteredEvents.length}
          totalCount={events.length}
          onOpenMap={() => setIsMapOpen(true)}
        />

        {loading ? (
          <SharedEventsSkeleton />
        ) : filteredEvents.length > 0 ? (
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            {filteredEvents.map((event, index) => (
              <SharedEventCard
                key={getSharedEventKey(event, index)}
                event={event}
                isCopying={isEventCopyingForSelectedAdherent(event.id)}
                isCopied={isEventCopiedForSelectedAdherent(event.id)}
                copyDisabled={!selectedAdherentId}
                onCopy={() => handleCopyEvent(event.id)}
                onViewDetails={() => handleOpenDetails(event)}
              />
            ))}
          </div>
        ) : (
          <SharedEventsEmptyState onReset={resetFilters} />
        )}
      </section>

      <SharedEventDetailsModal
        isOpen={isDetailsOpen}
        event={selectedEvent}
        onClose={handleCloseDetails}
        isCopying={
          selectedEvent
            ? isEventCopyingForSelectedAdherent(selectedEvent.id)
            : false
        }
        isCopied={
          selectedEvent
            ? isEventCopiedForSelectedAdherent(selectedEvent.id)
            : false
        }
        onCopy={() => selectedEvent && handleCopyEvent(selectedEvent.id)}
      />

      <SharedEventsMapModal
        isOpen={isMapOpen}
        events={events}
        onClose={() => setIsMapOpen(false)}
        onDepartmentSelect={handleDepartmentSelect}
      />

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default SharedEventsPage;
